import QueryRatebook from '../../graphql/queries/QueryRatebook';
import { mapGetters, mapActions } from 'vuex';
import { CALCULATOR_ERROR } from '../../components/Calculator/CalculatorDialogError';
import {
  DEFAULT_LEASE_TERM,
  GTM_EVENT_NAME_SME_LEASE_CALCULATOR_CAR,
  GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR,
  GTM_EVENT_STAGE_NO_RESULTS_FOUND,
  MAKE_MODELS_SEPARATOR,
  MAKES_SEPARATOR,
  MULTI_MODELS_SEPARATOR,
  RATEBOOK_SORT_MONTH_LEASE_PRICE,
  RATEBOOK_SORT_WEEK_LEASE_PRICE,
  REPAYMENT_MONTHLY,
  REPAYMENT_WEEKLY
} from '../../utils/constants';
import { cleanseSearchFilters } from '../../utils/cleanse-search-filters';
import { sanitizePairParam } from '../../utils/sanitize-pair-param';
import { normalizePairParam } from '../../utils/helpers';

export default {
  data: function () {
    return {
      searchFilters: {},
      isFetchRatebookLoading: false
    };
  },
  computed: {
    ...mapGetters({
      orgProfile: 'theme/getOrgProfile',
      ratebookOrgId: 'theme/ratebookOrgId',
      ratebook: 'ratebook/getRatebook',
      ratebookLoading: 'ratebook/getRatebookLoading'
    }),
    countryCode: function () {
      return this.orgProfile?.whiteLabel?.region ? this.orgProfile?.whiteLabel?.region : 'NZ';
    },
    calculatorManufacturers: function () {
      return this.orgProfile?.pricingConfiguration?.calculator?.isCalculatorEnabled
        ? [...this.orgProfile?.pricingConfiguration?.calculator?.manufacturersEnabled]
        : [];
    },

    queryParams: function () {
      let queryParams = { ...(this.$route.query || {}) };

      // normalizing Pair Param ensuring the backward compatibility
      queryParams = normalizePairParam(this.$route, this.$router, queryParams);

      const stringQueryFilter = ['sortBy', 'sortOrder', 'budgetType'];
      const integerQueryFilter = [
        'pageSize',
        'currentPage',
        'maxDuration',
        'minDuration',
        'vehicleMinRRP',
        'vehicleMaxRRP',
        'maxBudget',
        'minBudget'
      ];
      let formatParams = {};
      Object.keys(queryParams).map((key) => {
        const value = queryParams[key];
        if (key == 'pair' && typeof value === 'string') {
          const formatPair = value.split(MAKES_SEPARATOR).map((value) => {
            let [make, models] = value.split(MAKE_MODELS_SEPARATOR);

            if (models) {
              models = models.split(MULTI_MODELS_SEPARATOR);
            }

            return {
              make,
              models: models || []
            };
          });

          queryParams[key] = formatPair;
        } else if (stringQueryFilter.some((stringQueryKey) => stringQueryKey === key)) {
          queryParams[key] = value;
        } else if (integerQueryFilter.some((intQueryKey) => intQueryKey === key) && typeof value === 'string') {
          queryParams[key] = parseInt(value);
        } else {
          queryParams[key] = typeof value === 'string' ? value.split(',') : value;
        }

        formatParams = { ...formatParams, [key]: queryParams[key] };
      });

      return formatParams;
    }
  },
  methods: {
    ...mapActions({
      updateRatebook: 'ratebook/updateRatebook',
      updateRatebookLoading: 'ratebook/updateRatebookLoading',
      clearRatebook: 'ratebook/clearRatebook'
    }),
    formatRatebookStore(ratebook) {
      const { rows, totalCount } = ratebook;

      return {
        results: rows,
        currentPageResults: rows,
        resultCount: totalCount
      };
    },
    transformQueryParams() {
      return '';
    },
    async fetchRatebook(params, shouldUpdateRatebook = false) {
      this.clearRatebook();

      const defaultParams = {
        countryCode: this.countryCode,
        pageSize: 25,
        currentPage: 1,
        minDuration: params.minDuration || DEFAULT_LEASE_TERM,
        maxDuration: params.maxDuration || DEFAULT_LEASE_TERM
      };
      try {
        this.isFetchRatebookLoading = true;
        this.updateRatebookLoading(true);

        if (params?.vehicleMaxRRP === 120000) {
          delete params.vehicleMaxRRP;
        }
        const queryParams = { ...this.queryParams };
        if (queryParams?.vehicleMaxRRP === 120000) {
          delete queryParams.vehicleMaxRRP;
        }

        // Track if pair was explicitly set (even if empty) to distinguish between
        // "user cleared makes" vs "initial load without pair in params"
        const pairWasExplicitlySet = 'pair' in params;

        // If params.pair is explicitly set to empty array, it means user cleared makes
        // Remove it so it doesn't get sent to API
        const paramsCopy = { ...params };
        if (Array.isArray(paramsCopy.pair) && paramsCopy.pair.length === 0) {
          delete paramsCopy.pair;
        }

        // Sanitize pair param to filter out makes not in allowed manufacturers
        const safeParams = sanitizePairParam(paramsCopy, this.calculatorManufacturers);

        const searchFiltersRaw = {
          ...defaultParams,
          ...(this.calculatorManufacturers.length && !safeParams?.pair?.length && { vehicleMake: this.calculatorManufacturers }),
          // ...queryParams, SCOR-3141, resolving the internal route state issue
          // Only include pair from queryParams if:
          // 1. params doesn't have a pair property at all (not explicitly cleared)
          // 2. This ensures that when user removes makes (params.pair = []), we don't include old pair from URL
          ...(!pairWasExplicitlySet &&
            this.calculatorManufacturers?.length &&
            !safeParams.doNotIncludePair &&
            queryParams?.pair?.length && {
              pair: queryParams.pair.filter(({ make }) => this.calculatorManufacturers.includes(make))
            }),
          ...safeParams
        };

        if (searchFiltersRaw.sortBy === RATEBOOK_SORT_WEEK_LEASE_PRICE) {
          const repayment = searchFiltersRaw.budgetType || this.repayment || queryParams.budgetType || REPAYMENT_WEEKLY;

          if (repayment === REPAYMENT_MONTHLY) {
            searchFiltersRaw.sortBy = RATEBOOK_SORT_MONTH_LEASE_PRICE;
          }
        }

        const searchFilters = cleanseSearchFilters(searchFiltersRaw);

        searchFilters.ratebookOrgId = this.ratebookOrgId;

        const response = await this.$apollo.mutate({
          mutation: QueryRatebook,
          fetchPolicy: 'no-cache',
          client: 'boomiClient',
          variables: {
            searchFilters
          }
        });

        if (response?.data?.fetchRatebook && shouldUpdateRatebook) {
          const ratebook = response?.data?.fetchRatebook?.results;

          const formattedRatebook = this.formatRatebookStore(ratebook);

          if (!ratebook.totalCount) {
            /*
            this.$euiNotifications.emit({
              notification: CALCULATOR_ERROR
            });
            */
            setTimeout(() => {
              this.$gtm.trackEvent({
                event: GTM_EVENT_NAME_SME_LEASE_CALCULATOR_CAR,
                stage: GTM_EVENT_STAGE_NO_RESULTS_FOUND,
                data: {
                  ...GTM_EVENT_VALUES_LEASE_CALCULATOR_CAR
                }
              });
            }, 3000);
          }

          this.updateRatebook(formattedRatebook);
        }

        return response?.data?.fetchRatebook;
      } catch (error) {
        // eslint-disable-next-line
        console.error(JSON.stringify({ error }));

        this.$euiNotifications.emit({
          notification: CALCULATOR_ERROR
        });
      } finally {
        this.isFetchRatebookLoading = false;
        this.updateRatebookLoading(false);
      }
    }
  }
};
